import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { format } from 'date-fns';
import { Loader2, CalendarIcon, ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { PartySelector } from '@/app/components/common/PartySelector';
import { createCheque } from '@/app/services/chequeService';
import { ChequeRecordCreate } from '@/app/types/cheque';

// Form schema
const chequeFormSchema = z.object({
  party_id: z.number({ required_error: 'Party is required' }).positive(),
  cheque_number: z.string().min(1, 'Cheque number is required').max(50),
  cheque_date: z.date({ required_error: 'Cheque date is required' }),
  clearing_date: z.date({ required_error: 'Clearing date is required' }),
  amount: z
    .number({ required_error: 'Amount is required' })
    .positive('Amount must be greater than 0')
    .refine((val) => /^\d+(\.\d{1,2})?$/.test(val.toString()), {
      message: 'Max 2 decimal places allowed',
    }),
  bank_name: z.string().max(100).optional().nullable(),
});

type ChequeFormValues = z.infer<typeof chequeFormSchema>;

const RecordChequePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<ChequeFormValues>({
    resolver: zodResolver(chequeFormSchema),
    defaultValues: {
      party_id: undefined,
      cheque_number: '',
      cheque_date: new Date(),
      clearing_date: new Date(),
      amount: 0,
      bank_name: '',
    },
  });

  const createChequeMutation = useMutation({
    mutationFn: (data: ChequeRecordCreate) => createCheque(data),
    onSuccess: (data) => {
      toast.success(`Cheque ${data.cheque_number} recorded successfully`);
      queryClient.invalidateQueries({ queryKey: ['cheques'] });
      navigate('/cheques/list');
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to record cheque';
      toast.error(errorMsg);
    },
  });

  const onSubmit = (values: ChequeFormValues) => {
    const payload: ChequeRecordCreate = {
      party_id: values.party_id,
      cheque_number: values.cheque_number,
      cheque_date: format(values.cheque_date, 'yyyy-MM-dd'),
      clearing_date: format(values.clearing_date, 'yyyy-MM-dd'),
      amount: Number(values.amount.toFixed(2)),
      bank_name: values.bank_name || null,
    };

    createChequeMutation.mutate(payload);
  };

  return (
    <div className="container mx-auto py-6 max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Record Cheque</h1>
          <p className="text-muted-foreground">Record a post-dated cheque (no accounting impact)</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cheque Details</CardTitle>
          <CardDescription>
            This will create a memo entry for tracking purposes only. The cheque can be cleared later
            by converting it to a payment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Party Selection */}
              <FormField
                control={form.control}
                name="party_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Party *</FormLabel>
                    <PartySelector
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select party"
                      disabled={createChequeMutation.isPending}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Cheque Number and Bank */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cheque_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cheque Number *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., 123456"
                          disabled={createChequeMutation.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bank_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bank Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          placeholder="e.g., HDFC Bank"
                          disabled={createChequeMutation.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Amount */}
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value || '0'))}
                        value={field.value?.toString() ?? ''}
                        placeholder="0.00"
                        disabled={createChequeMutation.isPending}
                        className="text-right"
                      />
                    </FormControl>
                    <FormDescription>The amount written on the cheque</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cheque_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Cheque Date *</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full justify-start text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                              disabled={createChequeMutation.isPending}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormDescription>The date written on the cheque</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clearing_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Expected Clearing Date *</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full justify-start text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                              disabled={createChequeMutation.isPending}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormDescription>When you expect to deposit/clear the cheque</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={createChequeMutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createChequeMutation.isPending}>
                  {createChequeMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Recording...
                    </>
                  ) : (
                    'Record Cheque'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RecordChequePage;